import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal, Upload, type LucideIcon } from 'lucide-react'
import { cn } from '@meno/ui/lib/utils'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@meno/ui/components/ui/dropdown-menu'

// Shared tile/row for everything publishable in the content libraries
// (playlists, layouts, stop screens, media assets, transitions — and whatever
// comes next). One footer contract everywhere: an inline primary-action icon
// (publish / stream) plus a 3-dot menu for the type-specific options (rename /
// duplicate / delete / restore / enlarge / …). Keeping the action row in one
// component is what stops the "publish is a button here but a menu item there"
// drift the libraries had before. Per-context differences are expressed purely
// through props: a different `publishIcon`, an omitted `onPublish`, extra
// `menu` entries, or a `footerExtra` slot.

export type CardMenuItem = {
  icon: LucideIcon
  label: string
  onClick: () => void
  destructive?: boolean
}

// Stop propagation on action clicks so the card's onOpen doesn't fire when
// the user actually meant publish / rename / delete.
const stopThen = (fn: () => void) => (e: React.MouseEvent) => {
  e.stopPropagation()
  e.preventDefault()
  fn()
}

function CardActions({
  onPublish, publishIcon: PublishIcon = Upload, publishLabel, menu,
}: {
  onPublish?: () => void
  publishIcon?: LucideIcon
  publishLabel?: string
  menu: CardMenuItem[]
}) {
  const { t } = useTranslation()
  // Destructive entries sink to the bottom behind one separator.
  const firstDestructive = menu.findIndex(m => m.destructive)
  if (!onPublish && menu.length === 0) return null
  return (
    <div className="flex items-center gap-0.5 shrink-0">
      {onPublish && (
        <button
          type="button"
          onClick={stopThen(onPublish)}
          title={publishLabel ?? t('playlist.publish', 'Publish')}
          aria-label={publishLabel ?? t('playlist.publish', 'Publish')}
          className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <PublishIcon className="w-4 h-4" />
        </button>
      )}
      {menu.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t('common.more', 'More')}
            onClick={e => e.stopPropagation()}
            className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <MoreHorizontal className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44" onClick={e => e.stopPropagation()}>
            {menu.map((item, i) => (
              <Fragment key={item.label}>
                {item.destructive && i === firstDestructive && <DropdownMenuSeparator />}
                <DropdownMenuItem
                  onClick={stopThen(item.onClick)}
                  className={`gap-2 ${item.destructive ? 'text-destructive focus:text-destructive' : ''}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </DropdownMenuItem>
              </Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

// ── ContentCard — grid tile ────────────────────────────────────────────────
// `media` is the whole aspect-video area (tinted bg, thumb or fallback,
// overlay badges) — callers own its look; the card owns the frame + footer.
//
// Extension props keep every caller on one shell while letting each express
// its context: omit `onPublish` (Archive assets, folders), swap `publishIcon`
// (assets stream with Radio, not Upload), add a `footerExtra` slot (the audio
// card's inline scrubber), forward drag props (asset drag-and-drop) or
// `rootProps` (folder drop-target handlers), and merge `className` (the
// Transitions default/active ring).
export function ContentCard({
  title, subtitle, media, onOpen,
  onPublish, publishIcon, publishLabel, menu = [],
  footerExtra, className, rootProps,
  cardRef, draggable, onDragStart, onDragEnd,
}: {
  title: string
  subtitle?: string
  media: React.ReactNode
  onOpen?: () => void
  onPublish?: () => void
  publishIcon?: LucideIcon
  publishLabel?: string
  menu?: CardMenuItem[]
  /** Rendered full-width under the title/actions row (e.g. an audio scrubber). */
  footerExtra?: React.ReactNode
  /** Extra root classes — merged after the defaults (ring/border overrides). */
  className?: string
  /** Spread onto the root before the essential handlers (folder drop targets). */
  rootProps?: React.HTMLAttributes<HTMLDivElement>
  cardRef?: React.Ref<HTMLDivElement>
  draggable?: boolean
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void
}) {
  return (
    <div
      ref={cardRef}
      {...rootProps}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      onKeyDown={e => { if (onOpen && (e.key === 'Enter' || e.key === ' ')) onOpen() }}
      className={cn(
        'group relative text-start rounded-xl bg-card overflow-hidden hover:shadow-md transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30',
        onOpen && 'cursor-pointer',
        className,
      )}
    >
      {media}
      <div className="px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate leading-snug group-hover:text-primary transition-colors">
              {title}
            </p>
            {subtitle && <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>}
          </div>
          <CardActions onPublish={onPublish} publishIcon={publishIcon} publishLabel={publishLabel} menu={menu} />
        </div>
        {footerExtra && <div className="mt-1.5">{footerExtra}</div>}
      </div>
    </div>
  )
}

// ── ContentListRow — one-line list variant ─────────────────────────────────
// Small thumb (image or tinted type icon) + name/subtitle, same action pair.
export function ContentListRow({
  title, subtitle, thumb, icon: Icon, tintClassName, iconClassName,
  onOpen, onPublish, publishIcon, publishLabel, menu,
}: {
  title: string
  subtitle?: string
  thumb: string | null
  icon: LucideIcon
  /** bg tint for the thumb box when there's no image (e.g. bg-teal-500/10) */
  tintClassName?: string
  /** icon color inside the thumb box (e.g. text-teal-600) */
  iconClassName?: string
  onOpen: () => void
  onPublish?: () => void
  publishIcon?: LucideIcon
  publishLabel?: string
  menu: CardMenuItem[]
}) {
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onOpen() }}
      className="group flex items-center gap-3 rounded-xl bg-card px-3 py-2 hover:bg-muted/40 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30"
    >
      <div className={`w-14 h-9 rounded-lg overflow-hidden shrink-0 flex items-center justify-center border border-border/40 ${tintClassName ?? 'bg-muted'}`}>
        {thumb
          ? <img src={thumb} alt={title} className="w-full h-full object-cover" loading="lazy" />
          : <Icon className={`w-4 h-4 ${iconClassName ?? 'text-muted-foreground'}`} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{title}</p>
        {subtitle && <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>}
      </div>
      <CardActions onPublish={onPublish} publishIcon={publishIcon} publishLabel={publishLabel} menu={menu} />
    </li>
  )
}
